import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface VehicleCatalogRow {
  year: number;
  make: string;
  model: string;
  engine_packages: string;
  package_type: string;
}

const allPackageFiles = [
  { file: '/data/Vehicle_Package_Asian_master.csv', packageType: 'Asian' },
  { file: '/data/Vehicle_Package_Domestic_master.csv', packageType: 'Domestic' },
  { file: '/data/Vehicle_Package_Diesel_master.csv', packageType: 'Diesel' },
  { file: '/data/Vehicle_Package_Euro_master.csv', packageType: 'Euro' },
  { file: '/data/Vehicle_Package_EV_Master.csv', packageType: 'EV' },
  { file: '/data/Vehicle_Package_Exotic_master.csv', packageType: 'Exotic' },
  { file: '/data/geo_ymme.csv', packageType: 'Domestic' },
];

function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  
  result.push(current.trim());
  return result;
}

async function parseGeoCSV(text: string, packageType: string): Promise<VehicleCatalogRow[]> {
  const lines = text.split('\n').filter(line => line.trim());
  
  // Map to group engines by year/make/model
  const vehicleMap = new Map<string, {
    year: number;
    make: string;
    model: string;
    engines: string[];
  }>();

  // Skip header row (Year,Make,Model,Engine)
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',');
    if (values.length >= 4 && values[0]) {
      const year = parseInt(values[0]);
      const make = values[1];
      const model = values[2];
      const engine = values[3];

      const key = `${year}-${make}-${model}`;
      
      if (!vehicleMap.has(key)) {
        vehicleMap.set(key, { year, make, model, engines: [] });
      }
      
      vehicleMap.get(key)!.engines.push(engine);
    }
  }

  // Convert to final format with semicolon-separated engines
  const result: VehicleCatalogRow[] = [];
  for (const vehicle of vehicleMap.values()) {
    result.push({
      year: vehicle.year,
      make: vehicle.make,
      model: vehicle.model,
      engine_packages: vehicle.engines.join('; '),
      package_type: packageType
    });
  }

  return result;
}

async function parseCSV(text: string, packageType: string, isGeoFormat: boolean = false): Promise<VehicleCatalogRow[]> {
  // Handle Geo CSV format (one engine per row)
  if (isGeoFormat) {
    return parseGeoCSV(text, packageType);
  }

  // Standard format (semicolon-separated engines)
  const lines = text.split('\n').filter(line => line.trim());
  const rows: VehicleCatalogRow[] = [];

  // Skip header row
  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i]);
    
    if (values.length >= 4 && values[0]) {
      rows.push({
        year: parseInt(values[0]),
        make: values[1],
        model: values[2],
        engine_packages: values[3],
        package_type: packageType,
      });
    }
  }

  return rows;
}

export async function importVehicleCatalog(
  onProgress?: (message: string) => void,
  selectedPackages?: string[],
  clearAll: boolean = false
) {
  try {
    onProgress?.("Starting vehicle catalog import...");

    // Only clear ALL data if explicitly requested (e.g., from Import Vehicle Catalog page)
    if (clearAll) {
      onProgress?.("Clearing existing data...");
      const { error: deleteError } = await supabase
        .from('vehicle_catalog')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all

      if (deleteError) {
        console.error("Error clearing data:", deleteError);
        throw deleteError;
      }
    } else if (selectedPackages && selectedPackages.length > 0) {
      // Only clear data for the packages we're about to import
      onProgress?.("Clearing data for selected packages...");
      const { error: deleteError } = await supabase
        .from('vehicle_catalog')
        .delete()
        .in('package_type', selectedPackages);

      if (deleteError) {
        console.error("Error clearing package data:", deleteError);
        throw deleteError;
      }
    }

    let totalImported = 0;

    // Filter packages based on selection
    const packageFiles = selectedPackages 
      ? allPackageFiles.filter(p => selectedPackages.includes(p.packageType))
      : allPackageFiles;

    if (packageFiles.length === 0) {
      onProgress?.("No packages selected to import");
      return true;
    }

    // Import each CSV file
    for (const { file, packageType } of packageFiles) {
      onProgress?.(`Importing ${packageType} vehicles...`);

      try {
        const response = await fetch(file);
        const text = await response.text();
        
        // Detect Geo CSV format by filename
        const isGeoFormat = file.includes('geo_ymme.csv');
        const rows = await parseCSV(text, packageType, isGeoFormat);

        // Insert in batches of 500
        const batchSize = 500;
        for (let i = 0; i < rows.length; i += batchSize) {
          const batch = rows.slice(i, i + batchSize);
          
          const { error: insertError } = await supabase
            .from('vehicle_catalog')
            .insert(batch);

          if (insertError) {
            console.error(`Error inserting ${packageType} batch:`, insertError);
            throw insertError;
          }

          totalImported += batch.length;
          onProgress?.(`${packageType}: ${Math.min(i + batchSize, rows.length)}/${rows.length} rows`);
        }

        console.log(`✓ Imported ${rows.length} ${packageType} vehicles`);
      } catch (error) {
        console.error(`Error processing ${packageType}:`, error);
        throw error;
      }
    }

    onProgress?.(`✓ Import complete! ${totalImported} total vehicles imported`);
    toast.success(`Successfully imported ${totalImported} vehicles`);
    return true;

  } catch (error) {
    console.error("Import failed:", error);
    const anyErr = error as any;
    const errorMessage = anyErr?.message || anyErr?.error_description || anyErr?.hint || (error instanceof Error ? error.message : "Unknown error");
    onProgress?.(`✗ Import failed: ${errorMessage}`);
    toast.error(`Import failed: ${errorMessage}`);
    return false;
  }
}
